import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import cx from 'classnames';
import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import endOfMonth from 'date-fns/endOfMonth';
import { format, isAfter } from 'date-fns';
import { TextArea } from 'components/form';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { DateSelector } from './DateSelector.component';

import styles from './DatePicker.module.scss';

type DateSelectorProps = {
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  hasError?: boolean;
  helperText?: string;
  placeholder?: string;
};

type DatePickerProps = DateSelectorProps & {
  value: Date | null;
  onChange: (value: Date) => void;
  preselectMinDate?: boolean;
  className?: string;
  dateSelectorClassName?: string;
  dateInputClassName?: string;
};

type DateRangePickerProps = DateSelectorProps & {
  value: [Date, Date] | [null, null];
  onChange: (value: [Date, Date]) => void;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  hasError,
  helperText,
  preselectMinDate,
  placeholder,
  className,
  dateSelectorClassName,
  dateInputClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onDateChange = useCallback(
    (value: Date) => {
      setIsOpen(false);
      if (preselectMinDate && minDate) {
        onChange(endOfDay(value));
      } else {
        onChange(value);
      }
    },
    [preselectMinDate, minDate]
  );

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const dateContainerRef = useRef(null);
  useOnClickOutside({ ref: dateContainerRef, onOutsideClick: onClose });

  const dateLabel = useMemo(() => {
    if (value) {
      const selectedDate = format(value, 'dd-MMM-yyyy');
      if (preselectMinDate && minDate) {
        const startDate = new Date(minDate).toLocaleDateString();
        return `${startDate} - ${selectedDate}`;
      } else {
        return selectedDate;
      }
    } else {
      return '';
    }
  }, [value, preselectMinDate, minDate]);

  const rangeSelection = useMemo(() => {
    if (!minDate) return;

    const weeks1 = addDays(minDate, 7);
    const weeks2 = addDays(minDate, 14);
    const months1 = addDays(minDate, 30);
    const months2 = addDays(minDate, 60);
    const today = new Date();

    return (
      minDate &&
      preselectMinDate && (
        <div className={styles['days-range']}>
          {(isBefore(weeks1, today) ||
            weeks1.getTime() === today.getTime()) && (
            <button
              className={styles.selection}
              onClick={() => onDateChange(weeks1)}
            >
              {`1 week - till ${weeks1.toLocaleDateString()}`}
            </button>
          )}
          {(isBefore(weeks2, today) ||
            weeks2.getTime() === today.getTime()) && (
            <button
              className={styles.selection}
              onClick={() => onDateChange(weeks2)}
            >
              {`2 weeks - till ${weeks2.toLocaleDateString()}`}
            </button>
          )}
          {(isBefore(months1, today) ||
            months1.getTime() === today.getTime()) && (
            <button
              className={styles.selection}
              onClick={() => onDateChange(months1)}
            >
              {`1 month - till ${months1.toLocaleDateString()}`}
            </button>
          )}
          {(isBefore(months2, today) ||
            months2.getTime() === today.getTime()) && (
            <button
              className={styles.selection}
              onClick={() => onDateChange(months2)}
            >
              {`2 months - till ${months2.toLocaleDateString()}`}
            </button>
          )}
          <button
            className={styles.selection}
            onClick={() => onDateChange(today)}
          >
            {`Till today`}
          </button>
        </div>
      )
    );
  }, [minDate, preselectMinDate, onDateChange]);

  return (
    <>
      <div className={cx(styles.container, className)}>
        <div
          className={cx(styles['date-picker'], {
            [styles.range]: minDate && preselectMinDate,
          })}
        >
          <TextArea
            value={dateLabel}
            onChange={noop}
            label={label}
            isValid={!hasError}
            helperText={helperText}
            rows={1}
            iconName="calendar-new"
            onClick={isOpen ? onClose : onOpen}
            textAreaClassName={dateInputClassName}
            placeholder={placeholder}
          />
        </div>
        {isOpen && (
          <div
            className={cx(styles['date-range-selector'], dateSelectorClassName)}
            ref={dateContainerRef}
          >
            <DateSelector
              value={value}
              minimumDate={minDate}
              maximumDate={maxDate}
              onDateChange={onDateChange}
              preselectMinDate={preselectMinDate}
              className={styles['date-selector']}
            />
            {rangeSelection}
          </div>
        )}
      </div>
    </>
  );
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  hasError,
  helperText,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [rangeStartDate, setRangeStartDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setRangeStartDate(null);
    }
  }, [isOpen]);

  const onDateChange = useCallback(
    (value: Date) => {
      if (rangeStartDate) {
        onChange([startOfDay(rangeStartDate), endOfDay(value)]);
        setIsOpen(false);
      } else {
        setRangeStartDate(value);
      }
    },
    [onChange, rangeStartDate]
  );

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const dateLabel = useMemo(() => {
    const [startDate, endDate] = value;
    if (startDate !== null && endDate !== null) {
      const start = new Date(startDate).toLocaleDateString();
      const end = new Date(endDate).toLocaleDateString();

      return `${start} - ${end}`;
    } else {
      return '';
    }
  }, [value]);

  const onRangeChange = useCallback(
    (start: Date, end: Date) => {
      onChange([start, end]);
      onClose();
    },
    [onChange, onClose]
  );

  const rangeSelection = useMemo(() => {
    const today = new Date();
    const startOfToday = startOfDay(today),
      endOfToday = endOfDay(today);
    const yesterday = subDays(today, 1);
    const startOfYesterday = startOfDay(yesterday),
      endOfYesterday = endOfDay(yesterday);
    const startOf7Days = startOfDay(subDays(today, 7)),
      endOf7Days = endOfToday;
    const startOf30Days = startOfDay(subDays(today, 30)),
      endOfLast30Days = endOfToday;
    const lastWeekDay = subDays(today, 6);
    const startOfLastWeek = startOfWeek(lastWeekDay),
      endOfLastWeek = endOfWeek(lastWeekDay);

    const startOfLastMonth = startOfDay(new Date());
    startOfLastMonth.setDate(1);
    startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1);
    const endOfLastMonth = endOfDay(endOfMonth(startOfLastMonth));

    return (
      <div className={styles['days-range']}>
        <button
          className={styles.selection}
          onClick={() => onRangeChange(startOfToday, endOfToday)}
        >
          {`Today`}
        </button>
        {(!minDate || isAfter(startOfYesterday, minDate)) && (
          <button
            className={styles.selection}
            onClick={() => onRangeChange(startOfYesterday, endOfYesterday)}
          >
            {`Yesterday`}
          </button>
        )}
        {(!minDate || isAfter(startOf7Days, minDate)) && (
          <button
            className={styles.selection}
            onClick={() => onRangeChange(startOf7Days, endOf7Days)}
          >
            {`Last 7 days`}
          </button>
        )}
        {(!minDate || isAfter(startOf30Days, minDate)) && (
          <button
            className={styles.selection}
            onClick={() => onRangeChange(startOf30Days, endOfLast30Days)}
          >
            {`Last 30 days`}
          </button>
        )}
        {(!minDate || isAfter(startOfLastWeek, minDate)) && (
          <button
            className={styles.selection}
            onClick={() => onRangeChange(startOfLastWeek, endOfLastWeek)}
          >
            {`Last week`}
          </button>
        )}
        {(!minDate || isAfter(startOfLastMonth, minDate)) && (
          <button
            className={styles.selection}
            onClick={() => onRangeChange(startOfLastMonth, endOfLastMonth)}
          >
            {`Last month`}
          </button>
        )}
      </div>
    );
  }, [minDate, onRangeChange]);

  return (
    <>
      {isOpen && <div className={styles.backdrop} onClick={onClose}></div>}
      <div className={styles.container}>
        <div className={cx(styles['date-picker'], styles.range)}>
          <TextArea
            value={dateLabel}
            onChange={noop}
            label={label}
            isValid={!hasError}
            helperText={helperText}
            iconName={isOpen ? 'chevron-up' : 'chevron-down'}
            onClick={isOpen ? onClose : onOpen}
            textAreaClassName={styles['date-input']}
            placeholder={placeholder}
          />
        </div>
        {isOpen && (
          <div className={styles['date-range-selector']}>
            <DateSelector
              value={rangeStartDate ? null : value[1]}
              minimumDate={rangeStartDate || minDate}
              maximumDate={maxDate}
              onDateChange={onDateChange}
              className={styles['date-selector']}
              rangeStartDate={rangeStartDate || value[0]}
            />
            {rangeSelection}
          </div>
        )}
      </div>
    </>
  );
};
